import { useRef, useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import careers from "../../assets/careers.png";
import axios from "axios";
import { GET_CAREER_JOBS } from "../Api";
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshake, faPeopleGroup, faMagnifyingGlass, faHeart, faShieldAlt, faLightbulb, faGraduationCap, faPeopleArrows, faHeartPulse } from "@fortawesome/free-solid-svg-icons"
import { Users, Leaf, Globe } from "phosphor-react";

const Careers = () => {

  const jobsSectionRef = useRef(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    // Fetch jobs from the backend
    const fetchJobs = async () => {
      try {
        const response = await axios.get(GET_CAREER_JOBS, {
          params: { userSide: 'user' },
          headers: { 'Content-Type': 'application/json' },
        });
        console.log('Jobs:', response.data.jobs);
        setJobs(response.data.jobs);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    fetchJobs();
  }, []);

  const scrollToJobsSection = () => {
    jobsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      title: "Grow",
      desc: "We invest in your growth through structured learning paths, mentorship, and real-world challenges that help you evolve into the leader you aspire to be.",
      icon: Leaf,
      color: "text-green-600",
    },
    {
      title: "Belong",
      desc: "We nurture a welcoming and inclusive culture where everyone feels seen, heard, and valued—because true innovation starts with belonging.",
      icon: Users,
      color: "text-blue-600",
    },
    {
      title: "Impact",
      desc: "Be part of a purpose-driven team that’s building technology for environmental good—your work will have measurable, lasting impact on communities and the planet.",
      icon: Globe,
      color: "text-yellow-500",
    },
  ];

  // Extract only the largest ₹ amount from the string
  const extractSalary = (text) => {
    if (!text) return "—";

    // Match either ₹ followed by numbers OR just numbers with commas
    const matches = [...text.matchAll(/₹?\s?([\d,]+)/g)];

    if (matches.length === 0) return "—";

    const sorted = matches
      .map((m) => parseInt(m[1].replace(/,/g, ""), 10))
      .filter((num) => !isNaN(num))
      .sort((a, b) => b - a);

    if (sorted.length === 0) return "—";

    return `₹${sorted[0].toLocaleString("en-IN")}`;
  };


  return (
    <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row items-center justify-center py-20 gap-10 font-poppins">
        {/* Left Side - Text */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center h-full">
          <p className="text-lg sm:text-xl text-green-600 font-semibold mb-3 uppercase tracking-wide">
            WE'RE HIRING
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">
            Get hired and take the next step in your career.
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-10">
            Explore all the current openings across our dynamic teams and find where you fit best.
          </p>
          <button
            onClick={scrollToJobsSection}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold px-8 py-3 rounded-full text-lg transition-transform transform hover:scale-105 shadow-md"
          >
            View Open Roles
          </button>
        </div>

        {/* Right Side - Image */}
        <div className="flex justify-center lg:justify-end w-full lg:w-1/2">
          <img src={careers} alt="Careers" className="w-full max-w-[500px]" />
        </div>
      </div>




      {/* Sun Teknolozy Future Works Section */}
      <div className="text-center mb-20  font-sans">
        <h2 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-900 mb-6 tracking-tight">
          Sun Teknolozy is where the future works
        </h2>
        <p className="text-lg sm:text-xl text-center text-gray-600 max-w-6xl mx-auto mb-16">
          We’re not just building technology—we’re building futures. Join a workplace where you can grow with purpose, belong to something bigger, and make a real impact every day.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 text-left font-poppins">
          {features.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="relative border rounded-2xl h-80 w-100 shadow-md hover:shadow-xl group overflow-hidden transition"
              >
                {/* Icon Positioned Top Right */}
                <div className="absolute  top-2 right-6 ">
                  <Icon
                    size={150}
                    weight="regular"
                    className="text-black/20 transition-all duration-300 group-hover:opacity-0"
                  />
                  <Icon
                    size={150}
                    weight="fill"
                    className={`absolute top-0 left-0 transition-all duration-300 opacity-0 group-hover:opacity-100 ${item.color}`}
                  />
                </div>

                {/* Text Content at Bottom Left */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h3>
                  <p className="text-gray-700 text-base leading-relaxed">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Our Core Values Section */}
      <div className="my-28 font-poppins px-4 sm:px-8 md:px-16">
        <h3 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-900 mb-6 tracking-tight">
          Our Core Values
        </h3>
        <p className="text-lg sm:text-xl text-center text-gray-600 max-w-6xl mx-auto mb-16">
          At Sun Teknolozy, these values shape how we think, create, and collaborate. They are the reason behind our impact—and our inspiration to keep pushing forward.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-7xl mx-auto">
          {[
            {
              title: "Integrity",
              desc: "We act with honesty and accountability, staying true to our mission in every interaction.",
              icon: faShieldAlt,
              color: "text-blue-500",
            },
            {
              title: "Collaboration",
              desc: "We achieve more by working together—across people, platforms, and ideas.",
              icon: faPeopleGroup,
              color: "text-green-500",
            },
            {
              title: "Curiosity",
              desc: "We question, we explore, and we learn continuously to shape a smarter tomorrow.",
              icon: faMagnifyingGlass,
              color: "text-purple-500",
            },
            {
              title: "Empathy",
              desc: "We listen actively, care deeply, and put people first—in our work and in our world.",
              icon: faHeart,
              color: "text-pink-500",
            },
            {
              title: "Responsibility",
              desc: "We take ownership—of our code, our impact, and the trust placed in us.",
              icon: faHandshake,
              color: "text-yellow-500",
            },
            {
              title: "Innovation",
              desc: "We experiment boldly and build solutions that move the industry and planet forward.",
              icon: faLightbulb,
              color: "text-orange-500",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="group bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:scale-[1.015] duration-300"
            >
              <div className={`text-4xl mb-4 ${item.color} transition-transform group-hover:animate-shake`}>
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <h4 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h4>
              <p className="text-gray-600 leading-relaxed text-base">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>


      <div className="my-28 px-4 sm:px-8 md:px-16 font-poppins">
        <h3 className="text-4xl sm:text-5xl font-extrabold text-center text-gray-900 mb-6 tracking-tight">
          Working and Thriving
        </h3>
        <p className="text-lg sm:text-xl text-center text-gray-600 max-w-6xl mx-auto mb-16">
          At Sun Teknolozy, we’re building a culture where work empowers life—not the other way around. You’ll grow, balance, and thrive with the freedom and support you deserve.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {[
            {
              title: "On-Site Collaboration",
              desc:
                "We thrive on face-to-face teamwork, fostering real-time collaboration and stronger connections in an office-first culture.",
              icon: faPeopleArrows,
              color: "text-blue-500",
              bg: "bg-blue-50",
            },
            {
              title: "Learning & Growth",
              desc:
                "Your development is our priority. Gain access to workshops, upskilling, mentoring, and learning paths tailored to your journey.",
              icon: faGraduationCap,
              color: "text-green-500",
              bg: "bg-green-50",
            },
            {
              title: "Wellness Support",
              desc:
                "We care beyond the 9-to-5. Our wellness-first culture includes healthcare, flexible time off, and emotional well-being programs.",
              icon: faHeartPulse,
              color: "text-pink-500",
              bg: "bg-pink-50",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="relative bg-white/80 backdrop-blur-md border border-gray-200 rounded-3xl p-8 shadow-md hover:shadow-xl transition-all hover:scale-[1.02] group"
            >
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 text-2xl ${item.bg} ${item.color} group-hover:animate-shake`}
              >
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <h4 className="text-xl font-semibold mb-3 text-gray-900">{item.title}</h4>
              <p className="text-gray-600 leading-relaxed text-base">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Creating an Inclusive Environment - Clean Two Column Layout */}
      <div className="my-24 px-6 font-poppins hidden sm:block">
        <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-4">
          Creating an Inclusive Environment
        </h2>
        <p className="text-lg sm:text-xl text-gray-600 text-center max-w-6xl mx-auto mb-16">
          At Sun Teknolozy, inclusion is part of who we are. We’re building a culture where every voice matters, diversity is embraced, and everyone feels empowered to contribute and grow.
        </p>

        <div className="flex items-end justify-center gap-1 sm:gap-3">
          {[
            {
              desc: "We embrace diversity in all forms, knowing it drives innovation and better outcomes.",
              index: "01",
              height: "h-[180px]",
              bg: "bg-black",
              text: "text-white",
            },
            {
              desc: "We provide fair and equal access to opportunities, enabling every team member to succeed.",
              index: "02",
              height: "h-[220px]",
              bg: "bg-gray-300",
              text: "text-black",
            },
            {
              desc: "We foster an environment where authenticity is celebrated and everyone can belong.",
              index: "03",
              height: "h-[260px]",
              bg: "bg-white",
              text: "text-black",
            },
            {
              desc: "Accessibility is not an afterthought—it’s embedded into our tools, spaces, and thinking.",
              index: "04",
              height: "h-[360px]",
              bg: "bg-gray-100",
              text: "text-black",
            },
            {
              desc: "We value open communication and honest feedback as the foundation of trust and respect.",
              index: "05",
              height: "h-[300px]",
              bg: "bg-gray-50",
              text: "text-black",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`group relative w-[250px] ${item.height} ${item.bg} ${item.text} px-5 py-6 flex flex-col justify-between rounded-t-lg shadow-md transition-all duration-500 ease-in-out hover:h-[420px]`}
            >
              {/* Decorative Green Shape */}
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

              <div className="text-md font-medium leading-snug z-10">{item.desc}</div>
              <div className="text-4xl font-bold z-10">{item.index}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Jobs Section */}
      <section ref={jobsSectionRef} className="my-24 px-6 sm:px-12 font-poppins">
        {/* Section Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">
            Build Tomorrow. Start Today.
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-6xl mx-auto">
            Join Sun Teknolozy in shaping a smarter, cleaner, and more sustainable future. Discover roles where your ideas spark change and your growth never stops.
          </p>
        </div>
        {/* Job Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {jobs.length > 0 ? (
            jobs.map((job, index) => {
              const jobTime = new Date(job.createdAt._seconds * 1000);

              return (
                <Link
                  to={`/careers/jobdetails?jobid=${job.id}`}
                  className="bg-white border border-gray-200 rounded-3xl p-5 w-full shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col gap-4"
                >
                  {/* Top Row: Avatar + Title + Posted Date */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700 text-sm">
                      {(job.jobTitle || "ST").substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{job.jobTitle}</h3>
                      <p className="text-sm text-gray-500">
                        Posted {formatDistanceToNow(new Date(job.createdAt._seconds * 1000), { addSuffix: true })}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-base text-gray-600 line-clamp-3">{job.jobDescription}</p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {job.jobType && (
                      <span className="bg-gray-100 text-sm px-3 py-1 rounded-full text-gray-700 font-medium">
                        {job.jobType}
                      </span>
                    )}
                    {job.location && (
                      <span className="bg-gray-100 text-sm px-3 py-1 rounded-full text-gray-700 font-medium">
                        {job.location}
                      </span>
                    )}
                    {job.experience && (
                      <span className="bg-gray-100 text-sm px-3 py-1 rounded-full text-gray-700 font-medium">
                        {job.experience}
                      </span>
                    )}
                  </div>

                  {/* Salary + Apply Button */}
                  <div className="border-t pt-4 mt-4 flex justify-center items-center">
                    {/* <p className="text-xl font-semibold text-gray-800">
                      {extractSalary(job.salary)}
                    </p> */}

                    <button className="bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2 rounded-full font-semibold flex justify-center items-center gap-2 hover:scale-105 transition w-full">
                      ⚡ Apply
                    </button>

                  </div>
                </Link>
              );
            })
          ) : (
            <p className="text-gray-500 text-center col-span-full text-lg">
              No current openings. Please check back soon.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Careers;